import { Trans, useLingui } from "@lingui/react/macro";
import { addToast, Button, Image, Input, Link } from "@heroui/react";
import { useForm } from "@mantine/form";
import { api } from "@/lib/api-client";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { useState } from "react";
import { TurnstileCompat } from "@/Components/TurnstileCompat";

const ForgotPassword = () => {
  const { t } = useLingui();
  const {
    mutateAsync: postSendForgotPassword,
    isPending,
    processedErrorMessage,
    isSuccess,
  } = api.user.usePostSendForgotPassword();

  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const isTurnstileDisabled = import.meta.env.VITE_DISABLE_TURNSTILE === "true";

  const form = useForm({
    initialValues: {
      email: "",
    },

    validate: {
      email: (value) =>
        /^\S+@\S+$/.test(value.trim()) ? null : t`Neveljaven e-poštni naslov`,
    },
  });

  const onSubmit = async (values: typeof form.values) => {
    if (!turnstileToken && !isTurnstileDisabled) {
      addToast({
        title: t`Napaka`,
        description: t`Verifikacija z Captcha ni uspela. Osvežite stran in poskusite znova.`,
        color: "warning",
      });

      setTurnstileToken(null);
      setTurnstileKey((prev) => prev + 1);
      return;
    }

    try {
      await postSendForgotPassword({
        body: {
          ...values,
          email: values.email.trim(),
          turnstileToken: turnstileToken ?? undefined,
        },
      });

      addToast({
        title: t`Uspešno`,
        description: t`Povezava za ponastavitev gesla je bila poslana na vaš e-mail.`,
        color: "success",
      });
    } catch (error) {
      addToast({
        title: t`Napaka`,
        color: "danger",
      });
    }

    if (!isTurnstileDisabled) {
      setTurnstileToken(null);
      setTurnstileKey((prev) => prev + 1);
    }
  };

  return (
    <form onSubmit={form.onSubmit(onSubmit)} className="w-full">
      <div className="flex h-screen w-full flex-col items-center justify-center bg-white px-2 text-black">
        <Link href={"/login"}>
          <Image
            src="/lime-logo-full-horizontal.png"
            alt="logo"
            className="mb-4 h-auto w-72"
          />
        </Link>

        {!isSuccess ? (
          <>
            <div className="mt-2 flex flex-col gap-4">
              <h1 className="text-center text-xl font-semibold md:text-2xl">
                <Trans>Ponastavi svoje geslo</Trans>
              </h1>
              <p className="text-sm">
                <Trans>
                  Vnesite elektronski naslov, povezan z vašim računom, in
                  poslali vam bomo povezavo za ponastavitev gesla.
                </Trans>
              </p>
              <Input
                label={t`E-poštni naslov`}
                {...form.getInputProps("email")}
                className="mb-2"
                type="email"
              />

              {!isTurnstileDisabled && (
                <TurnstileCompat
                  key={turnstileKey}
                  siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                  onSuccess={(token) => setTurnstileToken(token)}
                  onExpire={() => setTurnstileToken(null)}
                  onError={(error) => {
                    console.error(error);
                    setTurnstileToken(null);
                  }}
                />
              )}

              <Button
                className="w-full"
                color="primary"
                type="submit"
                isLoading={isPending}
              >
                <Trans>Nadaljuj</Trans>
              </Button>

              <LimeAlert message={processedErrorMessage} />

              <div className="flex w-full flex-col items-center gap-1">
                <Link href="/login" className="mt-4 text-sm font-semibold">
                  <Trans>Nazaj na prijavo</Trans>
                </Link>
                <Link href="/register" className="text-sm">
                  <Trans>Še nimate računa? Registrirajte se!</Trans>
                </Link>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="mt-4 flex flex-col gap-4 px-2">
              <h1 className="font-semibold">
                <Trans>
                  Hvala, preverite svoj elektronski naslov za navodila za
                  ponastavitev gesla.
                </Trans>
              </h1>
              <p className="text-sm">
                <Trans>
                  Če niste prejeli elektronskega sporočila v 5 minutah,
                  preverite med vsiljeno pošto ali pošljite ponovno
                </Trans>
              </p>
            </div>
          </>
        )}
      </div>
    </form>
  );
};

export default ForgotPassword;
